<template>
  <section class="main flex flex-wrap w-full bg-black overflow-auto">
    <div class="text-white sans pt-32">
      <img class="text-center w-full mx-auto max-w-md block" src="@/assets/images/logo.svg">
      <h1 class="text-center font-bold text-xl mb-2 text-white">Settings & Debug</h1>
      <p>
        <a href="#" @click="exitDebug" class="text-center text-white mb-8 block pt-2">Exit Debug Area</a>
      </p>



      <div class="max-w-full rounded overflow-hidden shadow-lg bg-grey-light mb-8">

        <div class="px-6 py-4">
          <div class="font-bold text-xl mb-2 text-grey-darkest">Animation Triggers</div>
          <div class="text-grey-darker text-base">
            <p>Trigger an animation directly.</p>
          </div>

          <div class="pt-4">
            <button @click="sendOscGeneralAnimation" class="bg-white hover:bg-grey text-grey-darkest font-semibold py-2 px-4 shadow mb-8">
              Start CAC Animation
            </button>
          </div>
        </div>
      </div>
      

      <div class="max-w-full rounded overflow-hidden shadow-lg bg-grey-light mb-8">
        <div class="px-6 py-4">
          <div class="font-bold text-xl mb-2 text-grey-darkest">Media Server</div>
          <div class="py-1">
            <span class="inline-block bg-grey-lighter rounded-full pl-3 py-1 text-sm font-semibold text-grey-darker">Websocket State 
              <span v-if="wsConnectionState == 'open'" class="rounded-r-full bg-green text-white py-1 px-2 pr-4 capitalize">{{ wsConnectionState }}</span>
              <span v-else>
                <span class="rounded-r-full bg-red text-white py-1 px-2 pr-4 capitalize">{{ wsConnectionState }}</span>
              </span>
            </span>
            <span @click.prevent="restartWS" class="pl-2 text-sm text-grey-darker cursor-pointer">Restart</span>
          </div>
          <div class="pt-1 pb-4">
            <span class="inline-block bg-grey-lighter rounded-full pl-3 py-1 text-sm font-semibold text-grey-darker">Last D3 Connection Sync 
              <span v-if="hasD3Contact">
                <span class="rounded-r-full bg-green text-white py-1 px-2 pr-4 capitalize">{{ lastD3Contact | moment("from", "now") }}</span>
              </span>
              <span v-else class="rounded-r-full bg-red text-white py-1 px-2 pr-4 capitalize">Never</span>
            </span>
            <span v-if="hasD3Contact" class="pl-2 text-sm text-grey-darker">({{ lastD3Contact | moment("dddd, MMMM Do YYYY, h:mm:ss a") }})</span>
          </div>
          <div class="text-grey-darker text-base">
            <p>Kiosk's connect to the D3 media server over a Websocket Connection, which is bridged to the OSC UPD connection in D3. For media commands to function, the Websocket connection must be open.</p>
          </div>

          <div class="font-bold text-xl mb-2 text-grey-darkest mt-6">OSC Command Test</div>
          <div class="text-grey-darker text-base mb-4">
            <p>Send a test OSC Cue command to D3. This will force playback to jump to the specified Cue Id.</p>
            <p>Cue Ids, are always in the format X.Y.Z, eg. 1.1.0</p>
          </div>

          <input type="text" v-model="testOSCAddress" class="w-1/3 px-2 py-2 border-grey-light shadow mb-8 text-center"/>
          <input type="text" v-model="testOSCArgs" class="w-1/3 px-2 py-2 border-grey-light shadow mb-8 text-center"/>
          <button @click="sendOscDirect" class="bg-white hover:bg-grey text-grey-darkest font-semibold py-2 px-4 shadow mb-8">
            Send Direct OSC Command
          </button>
          
        </div>
      </div>

      <div class="max-w-full rounded overflow-hidden shadow-lg bg-grey-light mb-8">
        <div class="px-6 py-4">
          <div class="font-bold text-xl mb-2 text-grey-darkest">Application</div>
          <p class="text-grey-darker text-base">
            Reload and refresh the kiosk application. This will restart the currently running application, and pull the latest data from the remote source if it's available.
          </p>
          <button @click.prevent="reloadApp" :class="classReloading" class="bg-no-repeat bg-center rounded mt-4 py-4 px-4 bg-white hover:bg-red-dark text-grey-darker hover:text-white no-underline uppercase tracking-wide">
              <span :class="{ 'opacity-0' : appReloading }">Clear & Reload Kiosk</span>
            </button>
        </div>
      </div>

      <div class="max-w-full rounded overflow-hidden shadow-lg bg-grey-light mb-8">
        <div class="px-6 py-4">
          <div class="font-bold text-xl mb-2 text-grey-darkest">Data</div>
          <div class="py-4">
              <span class="inline-block bg-grey-lighter rounded-full pl-3 py-1 text-sm font-semibold text-grey-darker mr-2">Last Data Sync <span class="rounded-r-full bg-green text-white py-1 px-2 pr-4 capitalize">{{ dataLastRun | moment("from", "now") }}</span></span>
            </div>
          <p class="text-grey-darker text-base">The data running the kiosk is automatically updated from the remote CMS.</p>
          <p class="text-grey-darker text-base mt-2">Refreshing the data requires a working internet connection on this kiosk. If this is not available at the time of sync, the kiosk will use the last good data it receieved.</p>

          <button @click.prevent="refreshData" :class="classRefreshing" class="bg-no-repeat bg-center rounded mt-4 py-4 px-4 bg-white hover:bg-red-dark text-grey-darker hover:text-white no-underline uppercase tracking-wide">
            <span :class="{ 'opacity-0' : appRefreshing }">Manually Sync Data Now</span>
          </button>
        </div>
      </div>

      <div class="max-w-full rounded overflow-hidden shadow-lg bg-grey-light mb-8 clearfix">
        <div class="px-6 py-4">
          <div class="font-bold text-xl mb-2 text-grey-darkest">Kiosk Location</div>
          <p class="text-grey-darker text-base">CAC Kiosks are located in 2 positions relative to the central physical display - North and South.</p>
          <p class="text-grey-darker text-base mt-2">The different locations affect the rotation of the maps shown on the right hand side of all detail pages.</p>

          <a href="#" @click.prevent="setLocationNorth" :class="classMapNorth" class="max-w-xs mr-8 mt-4 rounded overflow-hidden shadow-lg float-left border-4 no-underline">
            <img class="w-full" src="@/assets/images/map-placeholder-north-small.png" alt="Sunset in the mountains">
            <div class="px-6 py-4">
              <div class="font-bold text-grey-darker  text-xl mb-2">North <span v-if="location == 'north'" class="font-light text-sm">(current)</span></div>
            </div>
          </a>
          <a href="#" @click.prevent="setLocationSouth" :class="classMapSouth" class="max-w-xs mt-4 mb-8 rounded overflow-hidden shadow-lg float-left border-4 no-underline">
            <img class="w-full" src="@/assets/images/map-placeholder-south-small.png" alt="Sunset in the mountains">
            <div class="px-6 py-4">
              <div class="font-bold text-grey-darker text-xl mb-2">South  <span v-if="location == 'south'" class="font-light text-sm">(current)</span></div>
            </div>
          </a>

        </div>
      </div>

      <div class="mb-12">
        <h3 class="mt-8 sans-bd pb-2">Information</h3>
        <p class="pb-2">Idle wait time: {{ idleWaitTime }} seconds</p>
        <p>Content controlled from: <a class="text-white" href="https://cac-cme-ss.architecture.org/admin">https://cac-cme-ss.architecture.org/admin</a></p>
      </div>

    </div>
  </section>
</template>
<script>
export default {
  name: 'DebugOverview',

  data () {
    return {
      appReloading: false,
      appRefreshing: false,
      testOSCCue: 110,
      testOSCAddress: '/d3/showcontrol/cue',
      testOSCArgs: 210,
      generalOSCCue: 2010
    }
  },

  computed: {

    hasD3Contact () {
      if (this.lastD3Contact !== null) return true
      return false
    },

    lastD3Contact () {
      return this.$store.getters['osc/lastD3Contact']
    },

    wsConnectionState () {
      return this.$store.getters['osc/wsConnection']
    },

    idleWaitTime () {
      return this.$store.getters['activity/idleWaitPeriod']
    },
    
    location () {
      return this.$store.getters['settings/location']
    },

    classReloading () {
      if (this.appReloading === true) return 'det__btn--progress'
      return false
    },
    classRefreshing () {
      if (this.appRefreshing === true) return 'det__btn--progress'
      return false
    },
    classMapNorth () {
      if (this.location === 'north') return 'border-red'
      return 'border-grey'
    },
    classMapSouth () {
      if (this.location === 'south') return 'border-red'
      return 'border-grey'
    },


    dataWaitPeriod () {
      return this.$store.getters['pages/dataWaitPeriod']
    },

    dataLastRun () {
      return this.$store.getters['pages/dataLastRun']
    },

    appWaitPeriod () {
      return this.$store.getters['pages/appWaitPeriod']
    },

    appLastRun () {
      return this.$store.getters['pages/appLastRun']
    },
  },

  methods: {

    exitDebug () {
      this.reloadApp()
    },

    restartWS () {
      this.$store.dispatch('osc/startOSCConnection')
    },

    sendOscGeneralAnimation () {
      this.$store.dispatch('osc/sendOscTriggerForced', this.generalOSCCue)
    },

    sendOscDirect () {
      this.$store.dispatch('osc/sendOscDirectMessage', {
        address: this.testOSCAddress,
        args: this.testOSCArgs
      })
    },

    toHome () {
      this.$router.push('/')
    },

    reloadApp () {
      this.appReloading = true
      window.location.href = '/'
    },

    refreshData () {
      this.appRefreshing = true
      this.$store.dispatch('pages/getAllContent')
    },

    setLocationNorth () {
      this.$store.dispatch('settings/setLocation', 'north')
    },

    setLocationSouth () {
      this.$store.dispatch('settings/setLocation', 'south')
    }
  }
}
</script>
